import { GithubIcon } from "@carbon6/c6-frontend-design";
import { useState } from "react";
import { useLocation } from "react-router";
import { beginGithubRedirectLogin } from "@/modules/auth/public";
import { isHttpClientError } from "@/shared/http";
import { createLogger } from "@/shared/logging";
import { AuthCard, type AuthCardStatus } from "./AuthCard";
import { LoginButton } from "./LoginButton";
import styles from "./LandingPage.module.css";

const logger = createLogger("app/landing");

export function LandingPage() {
  const [status, setStatus] = useState<AuthCardStatus>({ kind: "idle" });
  // 인증 가드(`RequireAuth`)가 미인증 진입을 여기로 보낼 때 원래 목적지를 함께 넘긴다.
  const returnTo = readReturnToFromLocationState(useLocation().state);

  const isLoginBusy = status.kind === "pending";

  async function handleLoginClick() {
    if (isLoginBusy) {
      return;
    }

    setStatus({ kind: "pending", message: "GitHub 로그인 페이지로 이동하는 중입니다." });

    try {
      await beginGithubRedirectLogin(returnTo);
    } catch (error) {
      logLoginError(error);
      setStatus({ kind: "failed", message: getLoginErrorMessage(error) });
    }
  }

  return (
    <AuthCard
      titleId="landing-title"
      title="Spawning Pool에 로그인합니다."
      subtitle="허용된 GitHub 조직의 멤버만 사용할 수 있습니다. 계속하려면 로그인해 주세요."
      status={status}
    >
      {/* 실패 시 이 버튼이 그대로 재시도 역할을 한다. */}
      <LoginButton
        className={styles.loginButton}
        size="md"
        disabled={isLoginBusy}
        busy={isLoginBusy}
        onClick={handleLoginClick}
      >
        <GithubIcon className={styles.loginIcon} />
        {isLoginBusy ? "로그인 중" : "GitHub 계정으로 로그인"}
      </LoginButton>
    </AuthCard>
  );
}

// history state 는 뒤로가기·새로고침으로 임의의 값이 올 수 있어 모양을 확인하고 읽는다.
// 경로 자체의 유효성은 `saveAuthReturnTo` 가 판정한다.
function readReturnToFromLocationState(state: unknown): string | undefined {
  if (typeof state !== "object" || state === null) {
    return undefined;
  }

  const from = (state as { from?: unknown }).from;
  return typeof from === "string" ? from : undefined;
}

function getLoginErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    switch (error.code) {
      // 이 origin 의 콜백이 Overlord allowlist 에 없다 — 주소를 바꿔 여는 것 말고는 방법이 없다.
      case "auth.github.redirect_uri.not_allowed":
        return "이 주소에서는 로그인을 시작할 수 없습니다. 허용된 접속 주소로 다시 열어 주세요.";
      case "auth.rate_limit.exceeded":
        return "로그인 요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.";
    }

    if (error.status === 404) {
      return "로그인을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.";
    }

    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "로그인을 완료하지 못했습니다.";
}

function logLoginError(error: unknown): void {
  if (isHttpClientError(error)) {
    logger.warn("Failed to start GitHub sign-in.", error.toLogContext());
    return;
  }

  logger.warn("Failed to process the GitHub sign-in redirect.", error);
}
